import { AfterViewInit, Component, EventEmitter, Inject, Output } from '@angular/core';

import { Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { MultiSelection } from '../../../shared/checkbox-list/entities/multi-selection.entity';
import { MemosFilters } from '../entities/memos-filters.entity';

import { UserTLR } from '../../humain/user/entities/user-tlr.entity';
import { UserService } from '../../humain/user/services/user.service';

import { AntenneTLR } from '../../structure/antenne/entities/antenne-tlr.entity';
import { AntenneService } from '../../structure/antenne/services/antenne.service';

export enum TypesPerimetresGeo {
	AVANCE = 0,
	REFERENT = 1,
	CAC = 2
}

export enum StatutMemo {
	VALIDE = 0,
	ARCHIVE = 1
}

@Component({
	selector: 'memos-filters',
	templateUrl: 'memos-filters.html',
	styleUrls: ['memos-filters.css']
})
export class MemosFiltersComponent implements AfterViewInit {
	// Elements de fonctionnements pour l'interface
	typesPerimetresGeo = TypesPerimetresGeo;
	typePerimetreOptions = [TypesPerimetresGeo.AVANCE, TypesPerimetresGeo.REFERENT, TypesPerimetresGeo.CAC];
	typePerimetreLabels: string[] = ["Filtres géographiques avancés", "Mon périmètre de référent de parc", "Mon périmètre CAC"];
	statutMemoOptions = [StatutMemo.VALIDE, StatutMemo.ARCHIVE];
	private statutMemoLabels: string[] = ["Valide", "Archivé"];
	getStatutsMemoLabel = (statut: StatutMemo) => this.statutMemoLabels[statut];
	statutShouldBeSelectedByDefaut = (statut: StatutMemo) => statut === StatutMemo.VALIDE;
	resetCommands = new Subject<void>();

	// Valeurs des filtres choisis par l'utilisateur
	filters: MemosFilters = new MemosFilters();

	// Evenement pour quand l'utilisateur demande l'application des filtres
	@Output() filterRequest = new EventEmitter<MemosFilters>();

	constructor( //
		@Inject(UserService) userService: UserService,
		@Inject(AntenneService) antenneService: AntenneService
	) {
		// Le champ perimetre geographique doit aussi etre reset quand une commande de reinitialisation arrive
		this.resetCommands.subscribe(
			() => {
				this.filters.perimetreGeographique = TypesPerimetresGeo.AVANCE;
			}
		)
		
		// Afin de simplifier le chargement initial et le re-chargement quand l'utilisateur reinitialise le formulaire, on va construire a un objet
		// MemosFilters par defaut a partir de l'utilisateur connecte et des informations des antennes.
		// Ceci permet de ne pas avoir a surveiller la fin de la re-initialisation de chaque champ de saisie pour etre sur que 'filters' a fini
		// d'etre re-initialise par le bindings, ce qui est plutot complique a mettre en oeuvre
		let userAntenne$ = userService.getCurrentUserTLR().pipe(
			map(
				(user: UserTLR) => user.role === "EXP" ? user.antenneId : null
			)
		);
		let antenneMap$ = antenneService.getAntenneTLRMapById();
		let defaultFilters$ = combineLatest([userAntenne$, antenneMap$]).pipe(
			map(
				(values: [number | null, Map<number, AntenneTLR>]) => {
					// Creation d'un filter qui ne filtre rien sauf le status (convient pour les CCE)
					let defaultFilter = new MemosFilters();
					defaultFilter.memoStatusSelection = new MultiSelection([StatutMemo.VALIDE], false)

					let antenneId = values[0];
					if (antenneId) {
						// S'il y a une restriction sur l'antenne, recuperer l'id de l'agence
						let antenne = values[1].get(antenneId);
						if (antenne === undefined) {
							throw new Error("Antenne #" + antenneId + " introuvable");
						}
						
						// Placer ensuite les filtres ad-hoc
						defaultFilter.agenceId = antenne.agenceId;
						defaultFilter.antenneIdsSelection = new MultiSelection([antenneId], false);
					}
					return defaultFilter;
				}
			)
		);
		
		// Enfin quand on recevera une demande de reinitialisation, on utilisera lancera la recherche avec ce filtre par defaut
		combineLatest([defaultFilters$, this.resetCommands]).subscribe(
			(values: [MemosFilters, void]) => {
				let defaultFilters = values[0];
				this.filterRequest.emit(defaultFilters);
			}
		);
	}

	ngAfterViewInit() {
		// NB: Attention! Comme resetCommands est potentiellement bind sur le sous-composant,
		// il faut attendre qu'il ait ete transmis avant d'envoyer le premier signal d'initialisation
		this.resetCommands.next();
	}

	search() {
		this.filterRequest.emit(this.filters);
	}

	onMemoStatusSelection(value: [StatutMemo[], boolean]) {
		this.filters.memoStatusSelection = new MultiSelection(value[0], value[1]);
	}

	resetFilters() {
		this.resetCommands.next();
	}
}